import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../_services/authentication.service';

/**
 * Who may use Devices: Labrador support only (backend superusers). Devices is an
 * internal tool, not a merchant feature, so the tab, the route and every API call are
 * gated on `/backend/v2/devices/access/` rather than on a store permission. ENG-5747.
 */
@Injectable({ providedIn: 'root' })
export class FleetAccessService {
  /** null until the first answer; then whether this sign-in is Labrador support. */
  readonly staff$ = new BehaviorSubject<boolean | null>(null);
  private askedFor: string | null = null;

  constructor(private http: HttpClient, private auth: AuthenticationService) {
    this.auth.currentUser.subscribe(user => {
      const token = user?.token || null;
      if (!token) { this.staff$.next(false); this.askedFor = null; return; }
      if (token !== this.askedFor) { this.askedFor = token; this.refresh(); }
    });
  }

  refresh(): void {
    this.http.get<{ staff: boolean }>(`${environment.apiUrl}/backend/v2/devices/access/`).pipe(
      map(r => !!r?.staff),
      catchError(() => of(false)),
    ).subscribe(staff => this.staff$.next(staff));
  }

  /** Resolves once the backend has answered. */
  isStaff(): Observable<boolean> {
    return this.staff$.pipe(filter((v): v is boolean => v !== null), take(1));
  }
}

/** Route guard for `/home/devices`: merchants are sent back to Home. */
export const staffGuard: CanActivateFn = () => {
  const access = inject(FleetAccessService);
  const router = inject(Router);
  return access.isStaff().pipe(map(ok => ok || router.createUrlTree(['/home'])));
};
